// Simple city distance service for radius-based show filtering
// This is a simplified implementation using approximate distances between major cities

const CITY_COORDINATES = {
  // Florida
  'Gainesville, FL': { lat: 29.6516, lng: -82.3248 },
  'Orlando, FL': { lat: 28.5383, lng: -81.3792 },
  'Jacksonville, FL': { lat: 30.3322, lng: -81.6557 },
  'Tampa, FL': { lat: 27.9506, lng: -82.4572 },
  'Miami, FL': { lat: 25.7617, lng: -80.1918 },
  'Tallahassee, FL': { lat: 30.4518, lng: -84.2807 },
  'Ocala, FL': { lat: 29.1872, lng: -82.1401 },
  
  // Texas
  'Denton, TX': { lat: 33.2148, lng: -97.1331 },
  'Dallas, TX': { lat: 32.7767, lng: -96.7970 },
  'Fort Worth, TX': { lat: 32.7555, lng: -97.3308 },
  'Austin, TX': { lat: 30.2672, lng: -97.7431 },
  'Houston, TX': { lat: 29.7604, lng: -95.3698 },
  'San Antonio, TX': { lat: 29.4241, lng: -98.4936 },
  'Plano, TX': { lat: 33.0198, lng: -96.6989 },
  'Irving, TX': { lat: 32.8140, lng: -96.9489 },
  
  // Georgia
  'Atlanta, GA': { lat: 33.7490, lng: -84.3880 },
  'Athens, GA': { lat: 33.9519, lng: -83.3576 },
  'Augusta, GA': { lat: 33.4735, lng: -82.0105 },
  'Savannah, GA': { lat: 32.0835, lng: -81.0998 },
  'Columbus, GA': { lat: 32.4609, lng: -84.9877 },
  
  // Kentucky
  'Owensboro, KY': { lat: 37.7719, lng: -87.1111 },
  'Louisville, KY': { lat: 38.2527, lng: -85.7585 },
  
  // California
  'Los Angeles, CA': { lat: 34.0522, lng: -118.2437 },
  'San Francisco, CA': { lat: 37.7749, lng: -122.4194 },
  'San Diego, CA': { lat: 32.7157, lng: -117.1611 },
  'Sacramento, CA': { lat: 38.5816, lng: -121.4944 },
  'Oakland, CA': { lat: 37.8044, lng: -122.2712 },
  'Berkeley, CA': { lat: 37.8715, lng: -122.2730 },
  
  // New York
  'New York, NY': { lat: 40.7128, lng: -74.0060 },
  'Brooklyn, NY': { lat: 40.6782, lng: -73.9442 },
  'Buffalo, NY': { lat: 42.8864, lng: -78.8784 },
  'Rochester, NY': { lat: 43.1566, lng: -77.6088 },
  'Syracuse, NY': { lat: 43.0481, lng: -76.1474 },
  
  // Others
  'Chicago, IL': { lat: 41.8781, lng: -87.6298 },
  'Nashville, TN': { lat: 36.1627, lng: -86.7816 },
  'Memphis, TN': { lat: 35.1495, lng: -90.0490 },
  'New Orleans, LA': { lat: 29.9511, lng: -90.0715 },
  'Birmingham, AL': { lat: 33.5186, lng: -86.8104 },
  'Charlotte, NC': { lat: 35.2271, lng: -80.8431 },
  'Raleigh, NC': { lat: 35.7796, lng: -78.6382 },
  'Charleston, SC': { lat: 32.7765, lng: -79.9311 },
  'Richmond, VA': { lat: 37.5407, lng: -77.4360 },
  'Washington, DC': { lat: 38.9072, lng: -77.0369 },
  'Baltimore, MD': { lat: 39.2904, lng: -76.6122 },
  'Philadelphia, PA': { lat: 39.9526, lng: -75.1652 },
  'Boston, MA': { lat: 42.3601, lng: -71.0589 },
  'Portland, OR': { lat: 45.5152, lng: -122.6784 },
  'Seattle, WA': { lat: 47.6062, lng: -122.3321 },
  'Denver, CO': { lat: 39.7392, lng: -104.9903 },
  'Phoenix, AZ': { lat: 33.4484, lng: -112.0740 },
  'Las Vegas, NV': { lat: 36.1699, lng: -115.1398 },
  'Salt Lake City, UT': { lat: 40.7608, lng: -111.8910 },
  'Kansas City, MO': { lat: 39.0997, lng: -94.5786 },
  'St. Louis, MO': { lat: 38.6270, lng: -90.1994 },
  'Minneapolis, MN': { lat: 44.9778, lng: -93.2650 },
  'Detroit, MI': { lat: 42.3314, lng: -83.0458 },
  'Cleveland, OH': { lat: 41.4993, lng: -81.6944 },
  'Cincinnati, OH': { lat: 39.1031, lng: -84.5120 },
  'Indianapolis, IN': { lat: 39.7684, lng: -86.1581 },
  'Milwaukee, WI': { lat: 43.0389, lng: -87.9065 }
};

class CityDistanceService {
  // Calculate distance between two coordinates using Haversine formula
  calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(value) {
    return value * Math.PI / 180;
  }

  // Get all cities within a radius of the given city
  getCitiesWithinRadius(baseCity, baseState, radiusMiles) {
    const baseCityKey = `${baseCity}, ${baseState}`;
    const baseCoords = CITY_COORDINATES[baseCityKey];
    
    if (!baseCoords) {
      console.warn(`Coordinates not found for ${baseCityKey}, falling back to current city only`);
      return [{ city: baseCity, state: baseState, distance: 0 }];
    }

    const nearbyCities = [];
    
    Object.entries(CITY_COORDINATES).forEach(([cityStateKey, coords]) => {
      const distance = this.calculateDistance(
        baseCoords.lat, baseCoords.lng,
        coords.lat, coords.lng
      );
      
      if (distance <= radiusMiles) {
        const [city, state] = cityStateKey.split(', ');
        nearbyCities.push({
          city,
          state,
          distance: Math.round(distance)
        });
      }
    });

    // Sort by distance
    return nearbyCities.sort((a, b) => a.distance - b.distance);
  }

  // Get all supported cities (for debugging)
  getAllCities() {
    return Object.keys(CITY_COORDINATES).map(cityStateKey => {
      const [city, state] = cityStateKey.split(', ');
      return { city, state };
    });
  }
}

const cityDistanceService = new CityDistanceService();
export default cityDistanceService; 